import { ZonedDateTime } from '@internationalized/date';
import {
  ItemAliasSortOption,
  ItemAliasType,
} from '@rematter/frontend-lib-api-client';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';
import { parseZonedDateTime } from '~/shared/utils/dateTime';

export type ItemAliasRecord = {
  id: string;
  itemId: string;
  item: {
    id: string;
    name: string;
  };
  accountId: string;
  account: {
    id: string;
    name: string;
  };
  aliasName: string;
  aliasCode: string | null;
  aliasType: ItemAliasType;
  notes: string | null;
  timestamps: {
    createdAt: ZonedDateTime;
    updatedAt: ZonedDateTime;
  };
};

export const itemAliasesListQueryAtom = createQueryAtomFactory(
  'settings.itemAliasesList',
  (params: {
    ids?: string[];
    searchTerm?: string;
    itemIds?: string[];
    accountIds?: string[];
    aliasTypes?: ItemAliasType[];
    limit?: number;
    offset?: number;
    sorting?: ItemAliasSortOption[];
  }) => ({
    itemAliases: {
      __args: {
        filter: {
          ids: params.ids,
          searchTerm: params.searchTerm,
          itemIds: params.itemIds,
          accountIds: params.accountIds,
          aliasTypes: params.aliasTypes,
        },
        sorting: params.sorting,
        pagination: {
          limit: params.limit ?? 50,
          offset: params.offset ?? 0,
        },
      },
      entries: {
        id: true,
        itemId: true,
        item: {
          id: true,
          name: true,
        },
        accountId: true,
        account: {
          id: true,
          name: true,
        },
        aliasName: true,
        aliasCode: true,
        aliasType: true,
        notes: true,
        timestamps: {
          createdAt: true,
          updatedAt: true,
        },
      },
      page: {
        total: true,
      },
    },
  }),
  {
    select: (data): { itemAliases: ItemAliasRecord[]; totalCount: number } => {
      if (!data?.itemAliases?.entries) {
        return { itemAliases: [], totalCount: 0 };
      }

      return {
        itemAliases: data.itemAliases.entries.map((alias) => ({
          id: alias.id,
          itemId: alias.itemId,
          item: { id: alias.item.id, name: alias.item.name },
          accountId: alias.accountId,
          account: { id: alias.account.id, name: alias.account.name },
          aliasName: alias.aliasName,
          aliasCode: alias.aliasCode ?? null,
          aliasType: alias.aliasType as ItemAliasType,
          notes: alias.notes ?? null,
          timestamps: {
            createdAt: parseZonedDateTime(alias.timestamps.createdAt),
            updatedAt: parseZonedDateTime(alias.timestamps.updatedAt),
          },
        })),
        totalCount: data.itemAliases.page?.total ?? 0,
      };
    },
  },
);
